import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useCustomPush } from '../CustomLink/CustomLink.hooks';
import * as S from './CatalogueGrid.styles';
import { CatalogueListItem } from '@/components/CatalogueListItem/CatalogueListItem';
import { CatalogueDrawer } from '@/components/CatalogueDrawer/CatalogueDrawer';
import {
  Category,
  GetRootCategoriesResponse,
} from '@/api/types/categories.types';
import {
  SCROLL_GAP,
  SCROLL_STEP,
  SCROLL_THRESHOLD,
  ALL_CATEGORIES_ID,
  ALL_SHOPS_ID,
  STORAGE_KEY_REMOVING_DELAY,
} from '@/components/CatalogueGrid/CatalogueGrid.constants';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { GET_ROOT_CATEGORIES } from '@/api/queries/categories.queries';
import { ROUTES } from '@/constants/routes';
import { useParseQuery } from '@/hooks/useParseQuery/useParseQuery';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { categoriesMock } from '@/__e2e__/mock/categoriesMock';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { useResponsive } from '@/hooks/useResponsive';
import { useServerDataContext } from '@/contexts';
import { useScroller } from '@/hooks/useScroller';

interface CatalogueGridProps {
  isHidden?: boolean;
}

export const CatalogueGrid: FC<CatalogueGridProps> = ({ isHidden }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const {
    rootCategories: serverRootCategories,
    currentCategory: serverCurrentCategory,
  } = useServerDataContext();
  const customPush = useCustomPush();
  const { clearUrl, queryString } = useUrlAndHashFromPath();
  const isHomePage = clearUrl === ROUTES.home;
  const { isDesktop } = useResponsive();

  const {
    keywords,
    categoryId,
    price,
    regionId,
    propertyOptions,
    userLegacyId,
    isShop,
  } = useParseQuery(isHomePage);
  const { currentCategory = serverCurrentCategory, isAllCategories } =
    useCurrentCategory();

  const currentCategoryId = categoryId || currentCategory?.id;
  const isBrowser = typeof window !== 'undefined';

  const {
    data: { categories = serverRootCategories?.categories || [] } = {},
    loading: isCategoriesLoading,
  } = useQuery<GetRootCategoriesResponse>(GET_ROOT_CATEGORIES, {
    notifyOnNetworkStatusChange: true,
    skip: !isBrowser,
    variables: isHomePage
      ? undefined
      : {
          keywords,
          filters: {
            categoryId: isAllCategories ? null : currentCategoryId,
            price,
            regionId,
            propertyOptions,
            userLegacyId,
            isShop,
          },
        },
  });

  const isLoading = !categories?.length && (!isBrowser || isCategoriesLoading);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const catalogueGridRef = useRef<HTMLDivElement>(null);

  const { isScrolled, isOverflowing, onScrollerClick } = useScroller(
    {
      containerRef: catalogueGridRef,
      step: SCROLL_STEP,
      threshold: SCROLL_THRESHOLD,
      gap: SCROLL_GAP,
    },
    [categories, isDesktop]
  );

  const hasScroller = !isLoading && isDesktop && isOverflowing;

  function toggleDrawer() {
    setIsDrawerOpen(prev => !prev);
  }

  function saveScrollLeftToLS() {
    if (catalogueGridRef.current?.scrollLeft) {
      localStorage.setItem(
        STORAGE_KEYS.CATALOGUE_SCROLL_LEFT,
        String(catalogueGridRef.current.scrollLeft)
      );
    }
  }

  function restoreScrollLeftFromLS() {
    const scrollLeft =
      Number(localStorage.getItem(STORAGE_KEYS.CATALOGUE_SCROLL_LEFT)) || 0;

    if (catalogueGridRef.current && scrollLeft) {
      catalogueGridRef.current.scrollLeft = scrollLeft;

      setTimeout(() => {
        localStorage.removeItem(STORAGE_KEYS.CATALOGUE_SCROLL_LEFT);
      }, STORAGE_KEY_REMOVING_DELAY);
    }
  }

  function goWithSavingDataToLS(path = '') {
    customPush(queryString ? `${path}?${queryString}` : path);
    if (isHomePage) {
      saveScrollLeftToLS();
    }
  }

  useEffect(() => {
    if (isHomePage) {
      restoreScrollLeftFromLS();
    } else if (catalogueGridRef.current) {
      catalogueGridRef.current.scrollLeft = 0;
    }
  }, [clearUrl, isHomePage]);

  const finalCategories = (
    isLoading ? categoriesMock(language).categories : categories
  ) as Category[];

  if (finalCategories.length === 0) {
    return null;
  }

  return (
    <S.Wrapper $isHomePage={isHomePage} $isHidden={isHidden}>
      {hasScroller && isScrolled && (
        <S.ScrollButton $side="left" onClick={onScrollerClick} />
      )}

      <S.Box
        data-testid="catalogue-grid"
        ref={catalogueGridRef}
        $isHomePage={isHomePage}
      >
        {isHomePage && !isDesktop && (
          <CatalogueListItem
            className="header-category all-categories"
            item={
              {
                id: ALL_CATEGORIES_ID,
                icon: 'all-categories.png',
                name: t('catalogue.catalogue'),
              } as Category
            }
            onClick={toggleDrawer}
            isHorizontalView
            isLoading={isLoading}
            hideOnDesktop
          />
        )}

        {finalCategories.map(item => (
          <CatalogueListItem
            key={item.id}
            item={item}
            onClick={() => goWithSavingDataToLS(`/${item.path}`)}
            isHorizontalView
            isLoading={isLoading}
            {...(isHomePage && { className: 'header-category' })}
          />
        ))}

        {isHomePage && (
          <CatalogueListItem
            data-stat="header-category-shop"
            item={
              {
                id: ALL_SHOPS_ID,
                icon: 'shop.png',
                name: t('catalogue.stores'),
                path: ROUTES.shops,
              } as Category
            }
            isHorizontalView
            isLoading={isLoading}
            isStoresIcon
            onClick={() => goWithSavingDataToLS(ROUTES.shops)}
          />
        )}
      </S.Box>

      {hasScroller && !isScrolled && (
        <S.ScrollButton $side="right" onClick={onScrollerClick} />
      )}

      <CatalogueDrawer
        isOpen={isDrawerOpen}
        close={toggleDrawer}
        hasSwitchCheckbox={false}
      />
    </S.Wrapper>
  );
};
